"use client"

import type { ComponentProps, ReactNode } from "react"
import {
  Dialog as UiDialog,
  DialogContent as UiDialogContent,
  DialogDescription as UiDialogDescription,
  DialogFooter as UiDialogFooter,
  DialogHeader as UiDialogHeader,
  DialogTitle as UiDialogTitle,
  DialogTrigger as UiDialogTrigger,
} from "@/components/ui/dialog"
import { LoadingOverlay } from "@/components/compass/LoadingOverlay"
import { cn } from "@/lib/utils"

export type DialogProps = {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  /**
   * Action em andamento: cobre o conteúdo com o Traço. O formulário continua
   * montado por baixo de propósito, para não perder o que foi digitado se a
   * action falhar.
   */
  busy?: boolean
  /** Texto anunciado enquanto `busy`. */
  busyLabel?: string
  className?: string
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  busy = false,
  busyLabel = "Salvando",
  className,
}: DialogProps) {
  return (
    <UiDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <UiDialogContent
        className={cn(
          /*
           * NUNCA passar `relative` aqui. O `cn` é twMerge: `relative` e
           * `fixed` são a MESMA família (position), então o `relative` do
           * wrapper apagava o `fixed` do primitivo. Sem `fixed`, o painel
           * voltava para o fluxo da página e o `top-[50%]` passava a medir
           * contra a altura do corpo: a caixa nascia colada no fim do
           * conteúdo, fora do centro e cortada pela borda da tela. O `fixed`
           * já é ancestral posicionado suficiente para o LoadingOverlay
           * (`absolute inset-0`) cobrir o painel inteiro.
           *
           * Altura: teto na viewport (1rem de respiro em cima e embaixo) e
           * corpo rolando por dentro. Modal alto (ex.: Editar empresa) era
           * cortado nas duas pontas, e o rodapé com os botões ficava
           * inalcançável porque nada rolava.
           */
          "flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden",
          "border-markv-surface-200 bg-markv-surface-50 text-markv-text-700",
          className,
        )}
        aria-busy={busy || undefined}
      >
        <LoadingOverlay open={busy} label={busyLabel} />
        {title ? (
          <UiDialogHeader className="shrink-0">
            <UiDialogTitle className="text-markv-text-700">{title}</UiDialogTitle>
            {description ? (
              <UiDialogDescription className="text-markv-text-400">
                {description}
              </UiDialogDescription>
            ) : null}
          </UiDialogHeader>
        ) : null}
        <div
          data-testid="dialog-body"
          className="markv-scroll min-h-0 flex-1 overflow-y-auto text-sm text-markv-text-700"
        >
          {children}
        </div>
        {footer ? <UiDialogFooter className="shrink-0">{footer}</UiDialogFooter> : null}
      </UiDialogContent>
    </UiDialog>
  )
}

export function DialogTrigger(props: ComponentProps<typeof UiDialogTrigger>) {
  return <UiDialogTrigger {...props} />
}
